import React, { useMemo } from "react";
import { colorConstant } from "../../styles/colorConstant/colorConstant";
import "./PosCustomCheckBox.css";

/**
 * PosCustomCheckBox
 *
 * A customizable checkbox component for POS and e-commerce UIs. Supports multiple style variants,
 * custom icons, and flexible layout options.
 *
 * @param {Object} props
 * @param {string} [props.id] - The id for the checkbox input.
 * @param {string} [props.name] - The name for the checkbox input.
 * @param {string|number} [props.value] - The value for the checkbox.
 * @param {string} [props.label] - The label to display next to the checkbox.
 * @param {Array} [props.checkedValue] - Array of checked values (for controlled checkbox groups).
 * @param {function} [props.onCheck] - Change handler for the checkbox.
 * @param {string} [props.variant] - Style variant for the checkbox.
 * @param {string} [props.width] - Width of the checkbox container.
 * @param {string} [props.height] - Height of the checkbox container.
 * @param {string} [props.radius] - Border radius.
 * @param {boolean} [props.transparent=false] - If true, background is transparent.
 * @param {string|number} [props.fontWeight] - Font weight for the label.
 * @param {string} [props.fontSize] - Font size for the label.
 * @param {boolean} [props.defaultIcon=true] - If true, uses default checkbox icon.
 * @param {boolean} [props.iconOnLeft=false] - If true, shows custom icon on the left.
 * @param {boolean} [props.iconOnRight=false] - If true, shows custom icon on the right.
 * @param {string} [props.checkBoxColor] - Custom color for the checkbox.
 * @param {string} [props.checkboxHeight] - Height for the custom checkbox icon.
 * @param {string} [props.checkboxWidth] - Width for the custom checkbox icon.
 * @param {string} [props.checkMarkColor] - Color for the checkmark.
 * @param {string} [props.padding="0.5rem"] - Padding for the checkbox container.
 * @returns {JSX.Element}
 *
 * @example
 * // Basic usage
 * <PosCustomCheckBox
 *   id="accept"
 *   name="terms"
 *   value="accepted"
 *   label="Accept Terms"
 *   checkedValue=[checkedItems]
 *   onCheck={handleCheck}
 * />
 *
 * @example
 * // With All the props
 * <PosCustomCheckBox
 *   id="vegan"
 *   name="diet"
 *   value="vegan"
 *   label="Vegan"
 *   checkedValue=[checkedItems]
 *   onCheck={handleCheck}
 *   variant="green-d"
 *   width="12px"
 *   height="12px"
 *   radius="2px"
 *   transparent={false}
 *   fontWeight=700
 *   fontSize="1rem"
 *   defaultIcon={false}
 *   iconOnLeft={true}
 *   iconOnRight={false}
 *   checkBoxColor="#4caf50"
 *   checkboxHeight="20px"
 *   checkboxWidth="20px"
 *   checkMarkColor="#fff"
 * />
 */

const PosCustomCheckBox = ({
  id,
  name,
  value,
  label,
  checkedValue,
  onCheck,
  variant,
  width,
  height,
  radius,
  transparent = false,
  fontWeight,
  fontSize,
  defaultIcon = true,
  iconOnLeft = false,
  iconOnRight = false,
  checkBoxColor,
  checkboxHeight,
  checkboxWidth,
  checkMarkColor,
  padding,
  disabled = false,
}) => {
  //Checking if the value is checked
  const isChecked = checkedValue.includes(value);

  //holding the value in varinat
  const appliedVariant = useMemo(() => {
    return variant;
  }, [variant, isChecked]);

  //Different varaints (styles) of the checkboxes.
  const variants = useMemo(() => {
    return (
      {
        "white-h": {
          background: `${transparent ? "transparent" : colorConstant.surfaceG0}`,
          color: colorConstant.surfaceG4,
          border: "none",
        },
        "white-d": {
          background: `${transparent ? "transparent" : colorConstant.surfaceWhite}`,
          color: colorConstant.surfaceG5,
          border: "none",
        },
        "blue-d": {
          background: colorConstant.blueB0,
          color: colorConstant.surfaceWhite,
          border: `1.5px solid ${colorConstant.blueB0}`,
        },
        "blue-s": {
          background: `${transparent ? "transparent" : colorConstant.blueB4}`,
          color: colorConstant.blueB0,
          border: `1.5px solid ${colorConstant.blueB0}`,
        },
        "blue-b4": {
          background: colorConstant.blueB4,
          color: colorConstant.surfaceG3,
          border: `none`,
        },
        "black-s": {
          background: colorConstant.surfaceWhite,
          color: colorConstant.surfaceG3,
          border: `1px solid ${colorConstant.surfaceG3}`,
        },
        "green-d": {
          background: colorConstant.greenG4,
          color: colorConstant.surfaceG3,
          border: `none`,
        },
        "red-light": {
          background: colorConstant.redLight,
          color: colorConstant.surfaceG3,
          border: `none`,
        },
      }[appliedVariant] || {}
    );
  }, [appliedVariant]);

  //Determinig whether to show custom icon or not
  const showCustomIcon = !defaultIcon && (iconOnLeft || iconOnRight);

  return (
    <div
      className="posCustomCheckboxWrapper"
      style={{
        ...variants,
        width: width,
        height: height,
        borderRadius: radius,
        fontWeight: fontWeight,
        fontSize: fontSize || "1rem",
        flexDirection: iconOnLeft ? "row" : "row-reverse",
        padding: padding || "0.5rem",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        id={id}
        name={name}
        value={value}
        checked={isChecked}
        onChange={onCheck}
        disabled={disabled}
        className={!defaultIcon ? "hideDefaultIcon" : ""}
      />
      {showCustomIcon && (
        <label
          htmlFor={id}
          className="posCustomCheckboxIconLabel"
          style={{ pointerEvents: disabled ? "none" : "auto" }}
        >
          <span
            className={`posCustomCheckboxIcon ${variant ? variant : ""} ${isChecked ? "act" : ""}`}
            style={{
              "--checkBoxColor": checkBoxColor || colorConstant.surfaceG3,
              height: checkboxHeight || "16px",
              width: checkboxWidth || "16px",
            }}
          >
            {isChecked && (
              <span
                className="checkMark"
                style={{
                  "--checkMarkColor": checkMarkColor || colorConstant.surfaceWhite,
                }}
              />
            )}
          </span>
        </label>
      )}
      <label
        htmlFor={id}
        className="posCustomCheckboxLabel"
        style={{ pointerEvents: disabled ? "none" : "auto" }}
      >
        {label}
      </label>
    </div>
  );
};

export default PosCustomCheckBox;
